import { FormGroup, Stack, FormRow } from '@cad-arc';
import { ConsolidatedLocationView, EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import {
    AgencyFieldsDisplayRow,
    AgencyFormGroupEditable,
} from '../../panels/entity-panels/entity-panel-components';
import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { globalAttributes } from '../../common/global-attributes';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { SubdivisionFields } from './event-panel-form-subdivision-fields';
import { useAgencyOptions } from './use-agency-options';

type EventPanelFormFireFieldsProps = {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
};

function useValuesFromProps(props: EventPanelFormFireFieldsProps) {
    const callForServiceValue = useWatch({
        control: props.form.control,
        name: 'callForService',
    });

    const location = useWatch({
        control: props.form.control,
        name: 'location',
        exact: true,
    }) as ConsolidatedLocationView | null;

    const agencyOptions = useAgencyOptions({
        location,
        baseOptions: props.eventPanelOptions.fireAgencyProfileOptions,
        agencyType: globalAttributes.agencyTypeGlobal.FIRE,
    });

    const eventForm = props.form;
    const eventPanelOptions = props.eventPanelOptions;

    const cfsIncludesFire = callForServiceValue?.agencyTypeGlobalAttrIds.includes(
        globalAttributes.agencyTypeGlobal.FIRE
    );
    return {
        eventForm,
        eventPanelOptions,
        agencyOptions,
        callForServiceValue,
        cfsIncludesFire,
    };
}

function DAreaAgencyFields({
    asDataListItem,
    ...props
}: EventPanelFormFireFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const localProps = useValuesFromProps({ ...props });

    return (
        <>
            <FormSelect
                asDataListItem={asDataListItem}
                asEditable={false}
                length="sm"
                control={localProps.eventForm.control}
                label="D-Area"
                name="fireDispatchArea"
                eventNotificationName="DISPATCH_AREA"
                options={props.eventPanelOptions.fireDispatchAreaOptions}
                showRequiredIndicator={!!localProps.cfsIncludesFire}
            />
            <FormSelect
                asDataListItem={asDataListItem}
                asEditable={false}
                control={localProps.eventForm.control}
                label="Agency"
                name="fireAgency"
                eventNotificationName="AGENCY"
                options={localProps.agencyOptions}
                showRequiredIndicator={!!localProps.cfsIncludesFire}
                length="md"
            />
        </>
    );
}

function RadioField({
    asDataListItem,
    ...props
}: EventPanelFormFireFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const localProps = useValuesFromProps({ ...props });

    if (enabledFeatures.radioChannels) {
        return (
            <FormSelect
                asDataListItem={asDataListItem}
                asEditable={false}
                control={localProps.eventForm.control}
                label="Primary Radio Channel"
                name="firePrimaryRadioChannel"
                options={props.eventPanelOptions.radioChannelOptions}
                length="md"
            />
        );
    }
    return <></>;
}

function FireFields({
    asDataListItem,
    ...props
}: EventPanelFormFireFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const localProps = useValuesFromProps({ ...props });

    return asDataListItem ? (
        <Stack spacing={0}>
            <AgencyFieldsDisplayRow>
                <SubdivisionFields
                    asDataListItem
                    eventClassPrefix="fire"
                    form={localProps.eventForm}
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <DAreaAgencyFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <RadioField
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            </AgencyFieldsDisplayRow>
        </Stack>
    ) : (
        <FormGroup spacing={1}>
            <FormRow>
                <SubdivisionFields eventClassPrefix="fire" form={localProps.eventForm} />
            </FormRow>
            <FormRow>
                <DAreaAgencyFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            </FormRow>
            <FormRow>
                <RadioField
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            </FormRow>
        </FormGroup>
    );
}

export function EventPanelFormFireFields(props: {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
}): React.ReactElement {
    const localProps = useValuesFromProps(props);

    return (
        <AgencyFormGroupEditable
            agency="Fire"
            viewMode={
                <FireFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            }
            editMode={
                <FireFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            }
        />
    );
}
